import { message } from "antd";
import { platformApi } from "../api";
import {
  toIndianMobile10,
  normalizeTelecmiNumber,
  getPiopiy,
  piopiyLogin,
} from "./piopiy";
import { getUUID } from "../_helpers/utils";

const SELECTED_KEY = "selectedTelecmiUserId";
const CALL_STARTED_TIMEOUT_MS = 8000;

/**
 * Extract dialable 10-digit phone from a notification payload.
 */
export const getNotificationDialPhone = (notification) => {
  const payload =
    typeof notification?.payload === "string"
      ? (() => {
          try {
            return JSON.parse(notification.payload);
          } catch {
            return {};
          }
        })()
      : notification?.payload || {};
  return (
    toIndianMobile10(payload.phone) ||
    toIndianMobile10(payload.from) ||
    null
  );
};

const fetchTeleUsers = async () => {
  const res = await platformApi.get("/api/telecmi/teleuser");
  return res.data?.data || [];
};

/**
 * Ensure this employee has a TeleCMI seat claimed (reuse or auto-claim first free).
 * Returns agent row (incl. teleCMIUserID) for Piopiy login.
 */
export const ensureTelecmiSeat = async (employeeName) => {
  try {
    const users = await fetchTeleUsers();
    const selectedId = localStorage.getItem(SELECTED_KEY);
    const selected = selectedId
      ? users.find((u) => u.id === selectedId)
      : null;

    if (
      selected &&
      selected.status === true &&
      selected.userName &&
      employeeName &&
      selected.userName === employeeName
    ) {
      return { ok: true, agentId: selected.id, agent: selected, users };
    }

    if (selectedId && (!selected || !selected.status)) {
      localStorage.removeItem(SELECTED_KEY);
    }

    const free = users.find((u) => !u.status);
    if (!free) {
      return {
        ok: false,
        error: "No free TeleCMI agent available. Try again later.",
      };
    }

    const toggleRes = await platformApi.post(
      `/api/telecmi/teleUser/toggleStatus/${free.id}`
    );
    if (toggleRes.data?.code !== 200) {
      return {
        ok: false,
        error: toggleRes.data?.msg || "Failed to claim TeleCMI agent",
      };
    }

    localStorage.setItem(SELECTED_KEY, free.id);
    return { ok: true, agentId: free.id, agent: free, users };
  } catch (err) {
    console.error("ensureTelecmiSeat error:", err);
    return {
      ok: false,
      error:
        err?.response?.data?.msg ||
        err?.message ||
        "Failed to login TeleCMI agent",
    };
  }
};

const ensurePiopiyForAgent = async (agent) => {
  const username = agent?.teleCMIUserID;
  if (!username) {
    throw new Error("TeleCMI agent missing teleCMIUserID");
  }

  let alreadyLogged = false;
  try {
    const state = JSON.parse(localStorage.getItem("piopiyLoginState") || "null");
    alreadyLogged =
      state?.username === username && Boolean(getPiopiy()?.call);
  } catch {
    alreadyLogged = false;
  }

  if (!alreadyLogged) {
    await piopiyLogin(null, username);
  }
};

const waitForAppDial = (detail) =>
  new Promise((resolve) => {
    let settled = false;
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener("telecmi-call-started", onStarted);
    };
    const finish = (result) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };
    const onStarted = (e) => {
      if (e?.detail?.ok) {
        finish({ ok: true });
      } else {
        finish({
          ok: false,
          error: e?.detail?.error || "Failed to place call",
        });
      }
    };
    const timer = setTimeout(() => {
      finish({
        ok: false,
        error: "Call timed out — TeleCMI softphone not ready. Try again.",
      });
    }, CALL_STARTED_TIMEOUT_MS);

    window.addEventListener("telecmi-call-started", onStarted);
    // Store pending dial, then force App to rebind listeners and flush
    window.dispatchEvent(
      new CustomEvent("telecmi-place-call", { detail })
    );
    window.dispatchEvent(new CustomEvent("piopiy-ready"));
  });

/**
 * Claim TeleCMI seat + Piopiy login, then let App dial after listeners are bound.
 */
export const ensureTelecmiThenCloudCall = async ({
  notification,
  employeeName,
  markRead = true,
} = {}) => {
  try {
    const phone10 = getNotificationDialPhone(notification);
    if (!phone10) {
      message.error("No customer phone on this notification");
      return { ok: false, error: "No phone" };
    }

    const dialNumber =
      normalizeTelecmiNumber(phone10) || `91${phone10}`;

    const seat = await ensureTelecmiSeat(employeeName);
    if (!seat.ok) {
      message.error(seat.error || "TeleCMI login failed");
      return seat;
    }

    await ensurePiopiyForAgent(seat.agent);

    window.dispatchEvent(
      new CustomEvent("telecmi-agent-selected", {
        detail: { agentId: seat.agentId, agent: seat.agent },
      })
    );

    const uniqueId = getUUID();
    const dialResult = await waitForAppDial({
      dialNumber,
      phone10,
      uniqueId,
    });

    if (!dialResult.ok) {
      message.error(dialResult.error || "Failed to place call");
      return dialResult;
    }

    if (markRead && notification?.id && !notification.isRead) {
      platformApi
        .post(`/api/notifications/${notification.id}/read`)
        .catch(() => {});
    }

    return { ok: true };
  } catch (err) {
    console.error("notification TeleCMI dial error:", err);
    message.error(
      err?.response?.data?.message || err?.message || "Failed to place call"
    );
    return { ok: false, error: err?.message };
  }
};
